// Thông báo (toast) và hộp xác nhận / nhập lý do — gọi kiểu mệnh lệnh: await confirm({...}), toast.ok('...').
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Btn } from './button';
import { Field, Select } from './form';
import { Icon } from './icon';
import { Modal } from './overlay';

function store<T>(initial: T) {
  let v = initial;
  const ls = new Set<() => void>();
  return {
    get: () => v,
    set: (n: T) => {
      v = n;
      ls.forEach((l) => l());
    },
    sub: (l: () => void) => (ls.add(l), () => ls.delete(l)),
  };
}

interface ToastItem {
  id: number;
  text: string;
  tone: '' | 'good' | 'bad';
}
const toasts = store<ToastItem[]>([]);
let seq = 0;
function push(text: string, tone: ToastItem['tone']) {
  const id = ++seq;
  toasts.set([...toasts.get(), { id, text, tone }].slice(-4));
  setTimeout(() => toasts.set(toasts.get().filter((t) => t.id !== id)), tone === 'bad' ? 6000 : 3500);
}
export const toast = { ok: (t: string) => push(t, 'good'), error: (t: string) => push(t, 'bad'), info: (t: string) => push(t, '') };

export function Toasts() {
  const list = useSyncExternalStore(toasts.sub, toasts.get);
  return (
    <div className="toasts" aria-live="polite">
      {list.map((t) => (
        <div className={`toast ${t.tone}`} key={t.id}>
          <Icon n={t.tone === 'bad' ? 'alert' : 'check'} s={16} />
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

interface DialogReq {
  title: string;
  body?: string;
  ok?: string;
  danger?: boolean;
  input?: string;
  required?: boolean;
  defaultText?: string;
  options?: [string, string][];
  resolve: (v: unknown) => void;
}
const dialog = store<DialogReq | null>(null);
function open<T>(d: Omit<DialogReq, 'resolve'>) {
  return new Promise<T>((resolve) => dialog.set({ ...d, resolve: resolve as (v: unknown) => void }));
}
/** Xác nhận: true / false. */
export const confirm = (d: { title: string; body?: string; ok?: string; danger?: boolean }) => open<boolean>(d);
/** Nhập văn bản (VD lý do khoá) hoặc chọn từ danh sách: chuỗi, hoặc null nếu huỷ. */
export const prompt = (d: { title: string; body?: string; ok?: string; danger?: boolean; input: string; required?: boolean; defaultText?: string; options?: [string, string][] }) => open<string | null>(d);

export function DialogHost() {
  const d = useSyncExternalStore(dialog.sub, dialog.get);
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    setText(d?.defaultText ?? '');
    if (d?.input) setTimeout(() => ref.current?.focus(), 30);
  }, [d]);
  if (!d) return null;
  const close = (v: unknown) => {
    dialog.set(null);
    d.resolve(v);
  };
  const cancel = () => close(d.input ? null : false);
  const needText = !!d.input && !!d.required && !text.trim();
  return (
    <Modal
      title={d.title}
      onClose={cancel}
      foot={
        <>
          <Btn onClick={cancel}>Quay lại</Btn>
          <Btn v={d.danger ? 'danger solid' : 'primary'} disabled={needText} onClick={() => close(d.input ? text.trim() : true)}>
            {d.ok ?? 'Xác nhận'}
          </Btn>
        </>
      }
    >
      <div className="col gap-12">
        {d.body && <p className="dim">{d.body}</p>}
        {d.input && (
          <Field label={d.input} req={d.required} htmlFor="dlg-text">
            {d.options ? (
              <Select id="dlg-text" value={text} onChange={setText} options={d.options} placeholder="Chọn…" />
            ) : (
              <textarea ref={ref} id="dlg-text" className="textarea" value={text} onChange={(e) => setText(e.currentTarget.value)} />
            )}
          </Field>
        )}
      </div>
    </Modal>
  );
}
